import { useEffect, useState } from 'react';
import { LocateFixed, Ruler, Radar } from 'lucide-react';
import { useMapController } from './MapShell.js';
import { useDepthStore } from './depthStore.js';
import { useSeamarkStore } from './seamarkStore.js';
import { useBaseLayerStore } from './baseLayerStore.js';
import { useMeasureStore } from '../nav/measureStore.js';
import { useAisSourcesStore } from '../ais/aisSourcesStore.js';
import { useAisStore } from '../ais/aisStore.js';
import type { FollowMode } from '../nav/followMachine.js';
import { msg } from '../../lib/messages.js';
import './controls.css';

/** Follow + zoom buttons. 44px targets, above the instruments (spec 02). */
export function MapControls(): JSX.Element | null {
  const controller = useMapController();
  const [follow, setFollow] = useState<FollowMode>('off');
  const depthOn = useDepthStore((s) => s.on);
  const toggleDepth = useDepthStore((s) => s.toggle);
  const seamarkOn = useSeamarkStore((s) => s.on);
  const toggleSeamark = useSeamarkStore((s) => s.toggle);
  const baseSource = useBaseLayerStore((s) => s.source);
  const measureActive = useMeasureStore((s) => s.active);
  const toggleMeasure = useMeasureStore((s) => s.toggle);
  // Rendered only when the server has BarentsWatch credentials (spec 16) —
  // otherwise the layer doesn't exist for this deploy, so no toggle to show.
  const aisAvailable = useAisSourcesStore((s) => s.available);
  const aisOn = useAisStore((s) => s.on);
  const aisDegraded = useAisStore((s) => s.degraded);
  const toggleAis = useAisStore((s) => s.toggle);

  // The ENC base chart already renders depth areas + soundings, so the separate
  // depth overlay is redundant: hide its button while ENC is active. The layer
  // wiring itself lives in MapShell/useMapLayers.
  const encActive = baseSource === 'enc';

  useEffect(() => {
    if (!controller) return;
    return controller.onFollowChange(setFollow);
  }, [controller]);

  if (!controller) return null;

  return (
    <div className="map-controls">
      <button
        type="button"
        className={`ctl ctl--depth${seamarkOn ? ' ctl--active' : ''}`}
        aria-pressed={seamarkOn}
        aria-label={msg.nav.seamarks}
        onClick={toggleSeamark}
      >
        <SeamarkIcon />
      </button>
      {!encActive && (
        <button
          type="button"
          className={`ctl ctl--depth${depthOn ? ' ctl--active' : ''}`}
          aria-pressed={depthOn}
          aria-label={msg.nav.depth}
          onClick={toggleDepth}
        >
          <DepthIcon />
        </button>
      )}
      {aisAvailable && (
        <button
          type="button"
          className={`ctl ctl--ais${aisOn ? ' ctl--active' : ''}${aisDegraded ? ' ctl--degraded' : ''}`}
          aria-pressed={aisOn}
          aria-label={msg.ais.toggle}
          onClick={toggleAis}
        >
          <Radar size={20} aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className={`ctl ctl--measure${measureActive ? ' ctl--active' : ''}`}
        aria-pressed={measureActive}
        aria-label={msg.nav.measure}
        onClick={toggleMeasure}
      >
        <Ruler size={20} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`ctl ctl--follow${follow === 'following' ? ' ctl--active' : ''}`}
        aria-pressed={follow === 'following'}
        aria-label={msg.nav.follow}
        onClick={() => controller.toggleFollow()}
      >
        <LocateFixed size={22} aria-hidden="true" />
      </button>
      <div className="ctl-group">
        <button
          type="button"
          className="ctl"
          aria-label={msg.nav.zoomIn}
          onClick={() => controller.zoomIn()}
        >
          +
        </button>
        <button
          type="button"
          className="ctl"
          aria-label={msg.nav.zoomOut}
          onClick={() => controller.zoomOut()}
        >
          −
        </button>
      </div>
    </div>
  );
}

function SeamarkIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M12 3v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path
        d="M8 9h8l-1.5 4h-5z"
        fill="currentColor"
        opacity="0.85"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M9 20h6M12 13v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function DepthIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        d="M3 8c2 0 2-2 4.5-2S10 8 12 8s2-2 4.5-2S19 8 21 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M3 14c2 0 2-2 4.5-2S10 14 12 14s2-2 4.5-2S19 14 21 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        opacity="0.6"
        strokeLinecap="round"
      />
      <text x="12" y="22" textAnchor="middle" fontSize="6" fill="currentColor">
        m
      </text>
    </svg>
  );
}

